import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Blocks,
  Bot,
  Code2,
  FileText,
  Gamepad2,
  Lock,
  Monitor,
  Network,
  ShieldCheck,
  Sparkles,
  Workflow,
} from 'lucide-react';
import SEO from '@/components/common/SEO';
import Header from '@/components/layout/Header';
import CorporateFooter from '@/components/layout/CorporateFooter';
import MainLayout from '@/components/layout/MainLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const trustPillars = [
  {
    icon: ShieldCheck,
    title: 'Trust by design',
    description:
      'MapleOS is built around visibility, auditability, and reversibility so AI actions are never hidden inside a black box.',
  },
  {
    icon: Lock,
    title: 'User-controlled context',
    description:
      'Sensitive context can be sealed, kept local, or shared selectively. Trust modes make access explicit instead of assumed.',
  },
  {
    icon: Workflow,
    title: 'Parallel AI workflows',
    description:
      'Run multiple AI tasks side by side, compare outputs, inspect execution, and reproduce results when decisions matter.',
  },
  {
    icon: Network,
    title: 'One living system',
    description:
      'Files, apps, knowledge, blueprints, tools, and reasoning all live in one ecosystem instead of being fragmented across tabs.',
  },
] as const;

const builderAudiences = [
  {
    title: 'Developers',
    description: 'Build, debug, and ship with a familiar IDE-style environment, terminal workflows, and modular runtimes.',
  },
  {
    title: 'System Builders',
    description: 'Compose multi-step workflows where tools, execution, and outputs remain inspectable inside the same system.',
  },
  {
    title: 'Researchers',
    description: 'Preserve evidence, reasoning context, notes, files, and knowledge over time with privacy-first foundations.',
  },
  {
    title: 'Creators',
    description: 'Write, ideate, and produce inside a workspace that protects flow and keeps AI subordinate to human direction.',
  },
] as const;

const appCategories = [
  { icon: Bot, label: 'AI', value: '11 apps', description: 'AI Chat, AI Center, task orchestration, knowledge, credits, and blueprints.' },
  { icon: Code2, label: 'Development', value: '7 apps', description: 'Code Editor, Terminal, MapleEngine, Docs Capture Studio, and builder tooling.' },
  { icon: Monitor, label: 'System', value: '14 apps', description: 'Control Panel, File Explorer, Task Manager, Messenger, network, and system utilities.' },
  { icon: FileText, label: 'Office', value: '10 apps', description: 'MapleWord, MapleSheet, MapleSlide, MapleCalc, MapleStats, MapleMoney, and more.' },
  { icon: Blocks, label: 'Explore', value: '6 apps', description: 'MapleGIS, MapleGofer, MapleStar, Navigator, Screenshots, and media tools.' },
  { icon: Gamepad2, label: 'Games', value: '6 apps', description: 'Retro-inspired entertainment including DOS Emulator, Cortex, and other native experiences.' },
] as const;

const editions = [
  {
    name: 'MapleOS 26',
    price: 'Free',
    description: 'Browser-native and desktop-ready cognitive workspace with web access and desktop installer included.',
  },
  {
    name: 'MapleOS Pro 26',
    price: '$199 / year',
    description: 'Annual pro edition with bare metal installer access and a one-time 5,000 AI credit grant.',
  },
  {
    name: 'MapleOS Enterprise 26',
    price: '$1,800 / year',
    description: 'Organizational edition with bare metal installer access and a one-time 50,000 AI credit grant.',
  },
] as const;

const screenshots = [
  {
    src: '/images/mapleos-hero-preview.png',
    title: 'MapleOS Desktop',
    description: 'A browser-native workspace that feels like a living operating system.',
  },
  {
    src: '/images/blueprints.png',
    title: 'AI Blueprints',
    description:
      'A structured launch surface for repeatable AI workflows that stay visible, understandable, and easy to relaunch with human oversight.',
  },

  {
    src: '/images/mapleos-ai-knowledge.png',
    title: 'AI Knowledge',
    description:
      'A dedicated knowledge workspace for managing knowledge bases, documents, and grounded context inside the MapleOS AI layer.',
  },
  {
    src: '/images/mapleos-mapleseed-vault.png',
    title: 'MapleSeed Vault',
    description:
      'Secure storage for seeds, reusable resources, and system-level assets inside the MapleOS development experience.',
  },
] as const;

export default function MapleOS() {
  return (
    <MainLayout>
      <SEO
        title="MapleOS"
        description="MapleOS is CanXP AI's immutable, human-first AI operating system built around trust, transparency, control, and parallel AI workflows."
        url="/mapleos"
      />

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <section className="relative overflow-hidden px-4 pb-20 pt-20 md:pb-28 md:pt-28">
          <div
            className="absolute inset-0 -z-20 bg-cover bg-center"
            style={{ backgroundImage: "url('/images/mapleos-hero-header.png')" }}
          />
          <div className="absolute inset-0 -z-10 bg-[linear-gradient(135deg,rgba(5,10,18,0.86),rgba(5,10,18,0.72)),radial-gradient(circle_at_top,rgba(220,38,38,0.18),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(220,38,38,0.12),transparent_28%)]" />
          <div className="container mx-auto max-w-6xl">

            <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary">
                  Product · MapleOS
                </Badge>
                <h1 className="mt-6 text-4xl font-semibold tracking-tight md:text-6xl">
                  The AI operating system built on trust, transparency, and control.
                </h1>
                <p className="mt-6 max-w-3xl text-lg leading-8 text-muted-foreground">
                  MapleOS is a living cognitive workspace for people who are done handing their files, attention,
                  and decision-making over to opaque platforms. It keeps apps, knowledge, files, blueprints, and AI
                  workflows in one inspectable system.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Badge className="bg-primary/10 text-primary">Immutable by design</Badge>
                  <Badge className="bg-primary/10 text-primary">Parallel AI workflows</Badge>
                  <Badge className="bg-primary/10 text-primary">Human-first operating model</Badge>
                </div>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg" className="gap-2">
                    <a href="https://mapleos.ai/" target="_blank" rel="noopener noreferrer">
                      Visit Official MapleOS Site
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link to="/enterprise">Talk to CanXP AI</Link>
                  </Button>
                </div>
              </div>

              <div className="rounded-[2rem] border border-border/70 bg-card/80 p-4 shadow-[0_24px_90px_-50px_rgba(220,38,38,0.75)] backdrop-blur">
                <img
                  src="/images/mapleos-hero-preview.png"
                  alt="MapleOS desktop preview"
                  className="w-full rounded-[1.25rem] border border-border/60 object-cover"
                />
                <div className="grid gap-4 px-2 pb-2 pt-5 sm:grid-cols-2">

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Positioning</p>
                    <p className="mt-2 text-sm text-muted-foreground">
                      MapleOS reframes AI as an accountable system that works with people, not over them.
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Built for</p>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Builders, researchers, creators, and teams that need visibility and control over AI work.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-8">
          <div className="container mx-auto max-w-6xl">
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {trustPillars.map((pillar) => {
                const Icon = pillar.icon;
                return (
                  <Card key={pillar.title} className="h-full border-border/70 bg-background/80">
                    <CardHeader>
                      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <CardTitle className="text-2xl tracking-tight">{pillar.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm leading-7 text-muted-foreground">{pillar.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-4 py-20">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-3xl">
              <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                A familiar desktop, rebuilt for AI
              </Badge>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-5xl">
                Everything people love about a desktop, redesigned around accountable intelligence.
              </h2>
              <p className="mt-5 text-lg leading-8 text-muted-foreground">
                MapleOS combines the feel of a classic desktop with AI-native control: inspectable chat, task
                management, knowledge bases with citations, blueprint-driven workflows, model selection, and office
                tools that keep humans in charge.
              </p>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {screenshots.map((shot) => (
                <Card key={shot.title} className="overflow-hidden border-border/70 bg-card/70">
                  <img src={shot.src} alt={shot.title} className="h-64 w-full object-cover object-top" />
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-semibold tracking-tight">{shot.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-muted-foreground">{shot.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-border/60 bg-card/30 px-4 py-20">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center">
              <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary">
                Built for builders
              </Badge>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-5xl">
                A system for developers, researchers, creators, and operators.
              </h2>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {builderAudiences.map((audience) => (
                <Card key={audience.title} className="h-full border-border/70 bg-background/80">
                  <CardHeader>
                    <CardTitle className="text-2xl tracking-tight">{audience.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm leading-7 text-muted-foreground">{audience.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-20">
          <div className="container mx-auto max-w-6xl">
            <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
              <div>
                <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                  App ecosystem
                </Badge>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-5xl">
                  One environment. Dozens of native capabilities.
                </h2>
                <p className="mt-5 text-lg leading-8 text-muted-foreground">
                  The public MapleOS catalog highlights more than 50 applications across AI, development, system,
                  office, exploration, and gaming experiences. It is positioned as a complete environment, not a
                  single-purpose AI wrapper.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {appCategories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <Card key={category.label} className="border-border/70 bg-background/80">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                            <Icon className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary/80">{category.label}</p>
                            <p className="text-xl font-semibold">{category.value}</p>
                          </div>
                        </div>
                        <p className="mt-4 text-sm leading-7 text-muted-foreground">{category.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 pb-8">
          <div className="container mx-auto max-w-6xl rounded-[2rem] border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-background p-8 md:p-10">
            <div className="max-w-3xl">
              <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary">
                Editions
              </Badge>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-5xl">
                MapleOS is available in free, pro, and enterprise editions.
              </h2>
              <p className="mt-5 text-lg leading-8 text-muted-foreground">
                The official MapleOS product lineup includes annual editions spanning browser access, desktop installs,
                and bare-metal deployment options for more advanced operators and organizations.
              </p>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {editions.map((edition) => (
                <Card key={edition.name} className="border-border/70 bg-background/85">
                  <CardHeader>
                    <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary/80">{edition.price}</p>
                    <CardTitle className="text-2xl tracking-tight">{edition.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm leading-7 text-muted-foreground">{edition.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-20">
          <div className="container mx-auto max-w-5xl text-center">
            <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
              Next step
            </Badge>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-5xl">
              Explore MapleOS or discuss a sovereign deployment roadmap.
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-muted-foreground">
              MapleOS is positioned as a browser-native and desktop-ready AI operating system that restores agency,
              transparency, and verifiable control to AI-assisted work.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="gap-2">
                <a href="https://mapleos.ai/" target="_blank" rel="noopener noreferrer">
                  Open MapleOS Website
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/maplenode">View MapleNode</Link>
              </Button>
            </div>
          </div>
        </section>

        <CorporateFooter />
      </div>
    </MainLayout>
  );
}
